import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../context/AuthContext';
import { X, Upload, Image as ImageIcon } from 'lucide-react';

const PROVINCES = ['SAN_JOSE', 'ALAJUELA', 'HEREDIA', 'CARTAGO', 'GUANACASTE', 'PUNTARENAS', 'LIMON'];
const CONDITIONS = ['NEW', 'USED'];
const DELIVERY_OPTIONS = [
  { id: 'pub', label: 'Recoger en persona (PUB)' },
  { id: 'dropoff', label: 'Entrega a domicilio' }
];
const DEFAULT_BRANDS = ['Honda', 'Yamaha', 'Kawasaki', 'Suzuki', 'KTM', 'TVS', 'Bajaj', 'AKT', 'Otro'];

export default function SellProduct() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState(DEFAULT_BRANDS);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [isAccessory, setIsAccessory] = useState(false);
  const [isConsumable, setIsConsumable] = useState(false);
  const [accessorySubcategories, setAccessorySubcategories] = useState([]);
  const [consumableSubcategories, setConsumableSubcategories] = useState([]);

  // Fetch brands on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const apiUrl = `/api`;
        const res = await fetch(`${apiUrl}/brands/visible`);
        const data = await res.json();
        if (data.brands && data.brands.length > 0) {
          setBrands([...data.brands, 'Otro']);
        }
      } catch (err) {
        console.error('Error fetching brands:', err);
      }
    };
    
    const fetchAccessorySubcategories = async () => {
      try {
        const apiUrl = `/api`;
        const res = await fetch(`${apiUrl}/accessories/subcategories`);
        const data = await res.json();
        setAccessorySubcategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching accessory subcategories:', err);
      }
    };

    const fetchConsumableSubcategories = async () => {
      try {
        const apiUrl = `/api`;
        const res = await fetch(`${apiUrl}/consumables/subcategories`);
        const data = await res.json();
        setConsumableSubcategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching consumable subcategories:', err);
      }
    };
    
    fetchBrands();
    fetchAccessorySubcategories();
    fetchConsumableSubcategories();
  }, []);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    negotiable: false,
    condition: 'NEW',
    brand: '',
    category_id: '',
    is_accessory: false,
    accessory_subcategory: '',
    color: '',
    size: '',
    province: 'SAN_JOSE',
    delivery_options: ['pub'],
    is_featured: false,
    product_type: '',
    consumable_subcategory: '',
    show_location: false
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'SELLER' && user.role !== 'ADMIN') {
      navigate('/');
    }
  }, [user]);

  useEffect(() => {
    if (selectedBrand) {
      const fetchModels = async () => {
        try {
          const apiUrl = `/api`;
          const res = await fetch(`${apiUrl}/categories/models/${selectedBrand}`);
          const data = await res.json();
          setCategories(Array.isArray(data) ? data : []);
        } catch (err) {
          console.error('Error fetching models:', err);
          setCategories([]);
        }
      };
      fetchModels();
    } else {
      setCategories([]);
    }
  }, [selectedBrand]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // Validate each file is under 1MB
    const MAX_SIZE = 1 * 1024 * 1024; // 1MB
    const oversized = files.filter(f => f.size > MAX_SIZE);
    if (oversized.length > 0) {
      alert(`Las siguientes imágenes exceden 1MB: ${oversized.map(f => f.name).join(', ')}`);
      return;
    }
    
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setImages(prev => [...prev, ...newImages].slice(0, 10));
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const removeImage = (index) => {
    const newImages = [...images];
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const handleDeliveryChange = (option) => {
    const current = form.delivery_options;
    if (current.includes(option)) {
      setForm({ ...form, delivery_options: current.filter(o => o !== option) });
    } else {
      setForm({ ...form, delivery_options: [...current, option] });
    }
  };

  const handleBrandChange = (e) => {
    const value = e.target.value;
    if (value === 'ACCESORIOS') {
      setIsAccessory(true);
      setIsConsumable(false);
      setSelectedBrand('');
      setCategories([]);
      setForm({ ...form, product_type: 'accessory', is_accessory: true, brand: '', category_id: '', accessory_subcategory: '', consumable_subcategory: '', color: '', size: '' });
    } else if (value === 'CONSUMIBLE') {
      setIsConsumable(true);
      setIsAccessory(false);
      setSelectedBrand('');
      setCategories([]);
      setForm({ ...form, product_type: 'consumable', is_accessory: false, brand: '', category_id: '', accessory_subcategory: '', consumable_subcategory: '', color: '', size: '' });
    } else {
      setIsAccessory(false);
      setIsConsumable(false);
      setSelectedBrand(value);
      setForm({ ...form, product_type: 'part', is_accessory: false, brand: value, category_id: '', accessory_subcategory: '', consumable_subcategory: '', color: '', size: '' });
      const fetchModels = async () => {
        try {
          const apiUrl = `/api`;
          const res = await fetch(`${apiUrl}/categories/models/${value}`);
          const data = await res.json();
          setCategories(Array.isArray(data) ? data : []);
        } catch (err) {
          setCategories([]);
        }
      };
      fetchModels();
    }
  };

  const handleCategoryChange = (e) => {
    setForm({ ...form, category_id: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (images.length < 1) {
      alert('Mínimo 1 foto obligatoria');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (Array.isArray(value)) formData.append(key, JSON.stringify(value));
        else formData.append(key, value);
      });
      images.forEach(img => formData.append('images', img.file));

      await api.post('/products', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert('Producto publicado exitosamente');
      navigate('/seller/products');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al publicar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="bg-secondary text-white py-8">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-3xl font-bold">📦 Publicar Producto</h1>
          <p className="text-white/80 mt-2">Completa todos los campos para publicar</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Photos */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Fotos (mínimo 1) *
            </h3>
            
            <div className="grid grid-cols-5 gap-3">
              {images.map((img, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden border-2 border-primary">
                  <img src={img.preview} alt="" className="w-full h-full object-cover" />
                  {i === 0 && (
                    <span className="absolute bottom-0 left-0 right-0 bg-primary text-white text-xs text-center py-1">
                      Principal
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              {images.length < 10 && (
                <label className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition">
                  <Upload className="w-8 h-8 text-gray-400" />
                  <span className="text-xs text-gray-500 mt-1">Agregar</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            
            <p className="text-sm mt-2 font-medium">
              {images.length === 0
                ? <span className="text-red-500">⚠️ Agrega al menos 1 foto</span>
                : <span className="text-green-600">✓ {images.length}/10 fotos • Máx 1MB por foto • Primera foto será la principal</span>
              }
            </p>
          </div>

          {/* Basic Info */}
          <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="font-bold text-lg">Información del producto</h3>
            
            <div>
              <label className="block font-medium mb-2">Título *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none"
                placeholder="Ej: Bobina de encendido Honda CB190R 2019"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-medium mb-2">Tipo de Artículo</label>
                <select
                  value={isAccessory ? 'ACCESORIOS' : isConsumable ? 'CONSUMIBLE' : selectedBrand}
                  onChange={handleBrandChange}
                  className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none"
                >
                  <option value="">Selecciona tipo</option>
                  <option value="ACCESORIOS">🔧 Accesorios</option>
                  <option value="CONSUMIBLE">🛢️ Consumibles</option>
                  {brands.map(b => <option key={b} value={b}>🔩 {b}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {isAccessory ? '→ Tipo: Accesorio' : isConsumable ? '→ Tipo: Consumible' : '→ Marca de moto'}
                </p>
              </div>
              {!isAccessory && selectedBrand && (
                <div>
                  <label className="block font-medium mb-2">Modelo de moto</label>
                  <select
                    value={form.category_id}
                    onChange={handleCategoryChange}
                    className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none"
                  >
                    <option value="">Selecciona modelo</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.model} {c.year_start && `- ${c.year_start}`}</option>
                    ))}
                  </select>
                </div>
              )}
              {isAccessory && (
                <>
                  <div>
                    <label className="block font-medium mb-2">Tipo de accesorio</label>
                    <select
                      value={form.accessory_subcategory}
                      onChange={(e) => setForm({ ...form, accessory_subcategory: e.target.value })}
                      className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none"
                    >
                      <option value="">Selecciona tipo</option>
                      {accessorySubcategories.map(sub => (
                        <option key={sub.id} value={sub.id}>{sub.label}</option>
                      ))}
                    </select>
                    {accessorySubcategories.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">No hay subcategorías configuradas</p>
                    )}
                  </div>
                  <div>
                    <label className="block font-medium mb-2">Color</label>
                    <input
                      type="text"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none"
                      placeholder="Ej: Negro, Rojo, Azul"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-2">Talla</label>
                    <input
                      type="text"
                      value={form.size}
                      onChange={(e) => setForm({ ...form, size: e.target.value })}
                      className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none"
                      placeholder="Ej: S, M, L, XL, Única"
                    />
                  </div>
                </>
              )}
              {isConsumable && (
                <>
                  <div>
                    <label className="block font-medium mb-2">Tipo de consumible</label>
                    <select
                      value={form.consumable_subcategory}
                      onChange={(e) => setForm({ ...form, consumable_subcategory: e.target.value })}
                      className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none"
                    >
                      <option value="">Selecciona tipo</option>
                      {consumableSubcategories.map(sub => (
                        <option key={sub.id} value={sub.id}>{sub.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block font-medium mb-2">Marca de moto compatible</label>
                    <select
                      value={selectedBrand}
                      onChange={(e) => {
                        setSelectedBrand(e.target.value);
                        setForm({ ...form, brand: e.target.value, category_id: '' });
                        if (e.target.value) {
                          const apiUrl = `/api`;
                          fetch(`${apiUrl}/categories/models/${e.target.value}`)
                            .then(r => r.json()).then(d => setCategories(Array.isArray(d) ? d : [])).catch(() => setCategories([]));
                        } else { setCategories([]); }
                      }}
                      className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none"
                    >
                      <option value="">Universal / Todas las marcas</option>
                      {brands.filter(b => b !== 'Otro').map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  {selectedBrand && categories.length > 0 && (
                    <div>
                      <label className="block font-medium mb-2">Modelo compatible</label>
                      <select
                        value={form.category_id}
                        onChange={handleCategoryChange}
                        className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none"
                      >
                        <option value="">Todos los modelos</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>{c.model} {c.year_start && `- ${c.year_start}`}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <label className="block font-medium mb-2">Descripción</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none"
                rows="4"
                placeholder="Describe detalles, compatibilidad, estado, etc."
              />
            </div>
          </div>

          {/* Price */}
          <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="font-bold text-lg">Precio</h3>
            
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block font-medium mb-2">Precio (₡) *</label>
                <input
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none"
                  placeholder="50000"
                  required
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.negotiable}
                    onChange={(e) => setForm({ ...form, negotiable: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span>Negociable</span>
                </label>
              </div>
            </div>

            {/* Featured option (only for PRO/PREMIUM) */}
            {(user?.subscription_plan === 'PRO' || user?.subscription_plan === 'PREMIUM') && (
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_featured}
                    onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                    className="w-5 h-5 accent-yellow-500"
                  />
                  <div>
                    <span className="font-bold text-black">⭐ Marcar como Destacado</span>
                    <p className="text-sm text-gray-600">Aparecerá en la sección de productos destacados</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer mt-3">
                  <input
                    type="checkbox"
                    checked={form.show_location}
                    onChange={(e) => setForm({ ...form, show_location: e.target.checked })}
                    className="w-5 h-5 accent-primary"
                  />
                  <div>
                    <span className="font-bold text-black">📍 Mostrar ubicación de la tienda</span>
                    <p className="text-sm text-gray-600">Los compradores verán el mapa con tu tienda en este producto</p>
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* Condition & Delivery */}
          <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="font-bold text-lg">Condición y entrega</h3>
            
            <div>
              <label className="block font-medium mb-2">Condición</label>
              <div className="flex gap-4">
                {CONDITIONS.map(c => (
                  <label
                    key={c}
                    className={`flex-1 p-4 border-2 rounded-xl text-center cursor-pointer ${
                      form.condition === c ? 'border-primary bg-primary/5' : 'border-gray-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="condition"
                      value={c}
                      checked={form.condition === c}
                      onChange={() => setForm({ ...form, condition: c })}
                      className="hidden"
                    />
                    <span className="text-2xl">{c === 'NEW' ? '✨' : '🔄'}</span>
                    <p className="font-medium mt-2">{c === 'NEW' ? 'Nuevo' : 'Usado'}</p>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-medium mb-2">Opciones de entrega</label>
              <div className="space-y-2">
                {DELIVERY_OPTIONS.map(opt => (
                  <label key={opt.id} className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={form.delivery_options.includes(opt.id)}
                      onChange={() => handleDeliveryChange(opt.id)}
                      className="w-5 h-5"
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-medium mb-2">Ubicación</label>
              <select
                value={form.province}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
                className="w-full border-2 rounded-xl px-4 py-3 focus:border-primary focus:outline-none"
              >
                {PROVINCES.map(p => <option key={p} value={p}>{p.replace('_', ' ')}</option>)}
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white font-bold py-4 rounded-xl hover:bg-primary-dark transition disabled:opacity-50"
          >
            {loading ? 'Publicando...' : '🚀 Publicar Producto'}
          </button>
        </form>
      </div>
    </div>
  );
}